<template>
  <PortalLayout section="Users" hideHeader>
    <div class="mt-8">
      <div class="pb-3">
        <BackButton to="../users"> Back to Users List </BackButton>
      </div>
      <Loading v-if="isPersonLoading" />
      <div v-else>
        <div v-if="person?.loginEmail && portalRegistration?.portalId > 0" class="border">
          <div class="p-4 pb-2">
            <div class="flex mb-4">
              <div class="flex-grow">
                <h1 class="text-xl">{{ person?.firstName }} {{ person?.lastName }}</h1>
                <span v-if="userIsDisabled" class="text-red-500">inactive</span>
                <span>
                  <h2 class="text-lg text-gray-700 inline-block align-middle">
                    {{ person?.loginEmail }}
                  </h2>
                  <BaseButton
                    :iconOnly="true"
                    class="align-middle ml-2"
                    @click="copyEmailToClipboard"
                  >
                    <svg
                      v-if="!isCopyClicked"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="size-4 align-middle p-0"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                      />
                    </svg>
                    <svg
                      v-else
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="currentColor"
                      class="size-4"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M11.35 3.836c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m8.9-4.414c.376.023.75.05 1.124.08 1.131.094 1.976 1.057 1.976 2.192V16.5A2.25 2.25 0 0 1 18 18.75h-2.25m-7.5-10.5H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V18.75m-7.5-10.5h6.375c.621 0 1.125.504 1.125 1.125v9.375m-8.25-3 1.5 1.5 3-3.75"
                      />
                    </svg>
                  </BaseButton>
                </span>
                <div class="text-sm text-gray-700">
                  {{ portalSiteRole?.roleAlias }}
                  <span v-if="portalSiteRole?.isStaffRole" class="text-gray-500">
                    (Staff)
                  </span>
                </div>
              </div>
              <div>
                <EditButton class="block mb-2 ml-auto" @click="editUser">
                  Edit User
                </EditButton>
                <div class="inline-block has-tooltip align-middle">
                  <span
                    class="tooltip rounded shadow-lg p-4 bg-gray-800 text-white mt-8 text-sm"
                  >
                    If you migrate a user now they will have to go through reset password
                    steps to login in future
                  </span>
                  <svg
                    v-if="!person.isMigrated"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-8 h-8 has-tooltip"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                    />
                  </svg>
                </div>

                <BaseButton class="inline-block mb-2 ml-auto" @click="loginAsUser">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-4 inline"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M13.5 10.5V6.75a4.5 4.5 0 1 1 9 0v3.75M3.75 21.75h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H3.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                    />
                  </svg>
                  {{ person.isMigrated ? "Login as User" : "Migrate and Login as User" }}
                </BaseButton>
              </div>
            </div>
            <BaseButton
              v-if="!person.isEmailVerified"
              class="mb-2 mx-1"
              @click="forceVerifyEmail"
            >
              Mark Email as Verified
            </BaseButton>
            <div>
              <EditButton class="mb-2 mx-1 lg:mb-0" @click="openPasswordPopup">
                Set Password
              </EditButton>
              <EditButton class="mb-2 mx-1 lg:mb-0" @click="openPassLinkPopup">
                Password Link
              </EditButton>
              <EditButton class="block lg:float-right" @click="openBulkAddPermissions">
                Bulk Add Permissions
              </EditButton>
            </div>
          </div>
          <div class="flex-row px-4 py-4 my-2 border-t font-bold">
            <div>
              Primary Tenant:
              <span class="text-sm font-normal font-mono">
                {{ tenants?.find((t) => t.value === portalRegistration.tenantId)?.name }}
              </span>
            </div>
            <div v-if="person?.primaryFloor">
              Primary Floor:
              <span class="text-sm font-normal font-mono">
                {{ person?.primaryFloor }}
              </span>
            </div>
            <div v-if="person?.mobilePhone">
              Mobile Phone:
              <span class="text-sm font-normal font-mono">{{ person?.mobilePhone }}</span>
            </div>
            <div v-if="person?.primaryPhone">
              Primary Phone:
              <span class="text-sm font-normal font-mono">
                {{ person?.primaryPhone }}
              </span>
            </div>
            <div>
              Has a Mobility Impairment:
              <span class="text-sm font-normal font-mono">
                {{ person.isMobilityImpaired ? "Yes" : "No" }}
              </span>
            </div>
            <div v-if="person?.contactEmail || person?.contactSms">
              <div>
                Alert Email:
                <span class="text-sm font-normal font-mono">
                  {{ person?.contactEmail }}
                </span>
              </div>
              <div>
                Alert SMS:
                <span class="text-sm font-normal font-mono">
                  {{ person?.contactSms }}
                </span>
              </div>
              <div>
                Alert Floors:
                <span class="text-sm font-normal font-mono">
                  {{ person?.alertSubscribedFloors ?? "All Floors" }}
                </span>
              </div>
            </div>
            <div v-if="person?.isTenantContact">Tenant Contact</div>
            <div v-if="person?.isVrAdmin">VR Admin</div>
            <div v-if="person?.isOldVrAdmin">VR Admin (legacy)</div>

            <!-- <div> 
              <a
                class="font-normal text-blue-500"
                :href="`/${property.siteCode}/login2/${person?.personId}/lkj234klj234klj2`"
                target="_blank"
              >
                Login as this User
              </a>
            </div> -->
          </div>
          <div class="flex-row px-4 py-4 my-2 border-t font-bold">
            <ul>
              Training programs:
              <div
                v-if="selectedTrainingProgramOptions.length === 0"
                class="text-sm font-normal font-mono"
              >
                None.
              </div>
              <li
                v-for="programId in selectedTrainingProgramOptions"
                class="text-sm font-normal font-mono list-disc ml-4"
              >
                {{ trainingProgramOptions.find((o) => o.value === programId)?.name }}
              </li>
            </ul>
            <div class="mt-4">
              Last 5 training results:
              <div
                v-if="lastFiveResults.length === 0"
                class="text-sm font-normal font-mono mb-2"
              >
                None.
              </div>
            </div>
            <div
              v-for="result in lastFiveResults"
              class="text-sm font-normal font-mono mb-2"
            >
              {{ result.dateCompleted }}
              <br />
              {{ result.trainingModuleTrainingProgramName }} >
              {{ result.trainingModuleName }} (Passed: {{ result.isPassingScore }})
            </div>
          </div>
          <div class="flex-row px-4 py-4 my-2 border-t font-bold">
            Last 5 logins:
            <div
              v-if="lastFiveLogins.length === 0"
              class="text-sm font-normal font-mono mb-2"
            >
              None.
            </div>
            <div
              v-for="login in lastFiveLogins"
              class="text-sm font-normal font-mono mb-2"
            >
              {{ formatDateTime(login.loginTime) }}
            </div>
            <div v-if="person?.createdDate" class="mt-8">
              Created:
              <span class="text-sm font-normal font-mono">
                {{ formatDateTime(person?.createdDate) }}
              </span>
            </div>
          </div>
          <div class="flex-row px-4 py-4 my-2 border-t font-bold">
            <ul>
              Permissions at this Portal:
              <div
                v-if="
                  currentPermissions.length === 0 &&
                  !personIsGrantedAccessToClient &&
                  !personIsGrantedAccessToCategory
                "
                class="text-sm font-normal font-mono"
              >
                None.
              </div>
              <li
                v-for="permissionType in currentPermissions"
                :key="permissionType"
                class="text-sm font-normal font-mono list-disc ml-4"
              >
                {{
                  portalPermissionOptions.find((o) => o.value === permissionType)?.name
                }}
              </li>
              <li
                v-if="personIsGrantedAccessToClient"
                class="text-sm font-normal font-mono list-disc ml-4"
              >
                Can View All Portals in Client
              </li>
              <li
                v-if="personIsGrantedAccessToCategory"
                class="text-sm font-normal font-mono list-disc ml-4"
              >
                Can View All Portals in Client
                {{ `${client?.categoryAlias ?? "Category"}` }}:
                <strong>
                  {{ personIsGrantedAccessToCategory }}
                </strong>
              </li>
            </ul>
          </div>
          <div class="flex-row px-4 py-4 my-2 border-t font-bold">
            <div class="flex justify-between mb-4">
              <div>All User Registrations:</div>
              <div>
                <BaseButton
                  class="bg-transparent text-sm"
                  @click="openRegistrationPermissions"
                >
                  Edit
                </BaseButton>
              </div>
            </div>

            <router-link
              :to="`/staff/${r.siteCode}/user/${person?.personId}`"
              class="font-normal block mt-2 w-full m:w-5/6 xl:w-3/4 2xl:w-1/2"
              v-for="r in allRegistrations"
            >
              <BaseButton
                class="flex w-full"
                :disabled="property.siteCode === r.siteCode"
                @click="bldgStore.tryGetBuildingData(r.siteCode)"
              >
                <i-heroicons-outline-check
                  class="w-6 h-6 mr-2 my-auto"
                  :class="property.siteCode === r.siteCode ? '' : 'invisible'"
                />
                <div class="flex-grow text-left">
                  {{ r.clientName }} <span v-if="r.category">> {{ r.category }}</span> >
                  {{ r.name }} ({{ r.siteCode }})
                  <span v-if="r.isHomePortal" class="font-bold"> (Home Portal)</span>
                </div>
              </BaseButton>
            </router-link>
          </div>
          <!-- Push Notification Devices -->
          <div class="flex-row px-4 py-4 my-2 border-t font-bold">
            Push Notification Devices:
            <div
              v-if="pushSubscriptions.length === 0"
              class="text-sm font-normal font-mono mb-2"
            >
              None.
            </div>
            <div
              v-for="sub in pushSubscriptions"
              :key="sub.pushSubscriptionId"
              class="text-sm font-normal font-mono mb-2 flex justify-between items-start"
            >
              <div>
                <div :title="sub.userAgent">{{ parseUserAgent(sub.userAgent) }}</div>
                <div class="text-xs text-gray-500">
                  Registered: {{ formatDateTimeLocal(sub.createdDate) }}
                  <span v-if="sub.lastSentDate">
                    | Last Sent: {{ formatDateTimeLocal(sub.lastSentDate) }}
                  </span>
                </div>
              </div>
              <div class="flex gap-2">
                <BaseButton
                  class="text-blue-600 text-xs"
                  :disabled="isSendingTestNotification"
                  @click="openTestNotificationPopup(sub)"
                >
                  Test
                </BaseButton>
                <BaseButton
                  class="text-red-600 text-xs"
                  :disabled="isDeletingSubscription"
                  @click="deletePushSubscription(sub.pushSubscriptionId)"
                >
                  Delete
                </BaseButton>
              </div>
            </div>
          </div>
        </div>
        <div v-else>
          <span class="bg-blue-200">Person Not Found for This Portal</span>
        </div>
      </div>
    </div>

    <!-- Test Push Notification Popup -->
    <BasePopup
      title="Send Test Notification"
      :is-visible="isTestNotificationPopupVisible"
      :is-full-width="false"
      @close="isTestNotificationPopupVisible = false"
    >
      <div class="flex flex-col">
        <div class="mb-4">
          <p>
            Send a test push notification to
            <strong>{{ testNotificationDevice }}</strong>?
          </p>
          <label class="block text-sm font-medium text-gray-700 mt-3">Message</label>
          <textarea
            v-model="testNotificationBody"
            rows="3"
            class="mt-1 w-full border border-gray-300 rounded p-2 text-sm"
          />
          <Loading v-if="isSendingTestNotification" />
          <div
            v-if="testNotificationResult"
            class="mt-3 p-2 rounded text-sm"
            :class="
              testNotificationResult === 'success'
                ? 'bg-green-100 text-green-800'
                : 'bg-red-100 text-red-800'
            "
          >
            {{
              testNotificationResult === "success"
                ? "Test notification sent successfully!"
                : testNotificationResult
            }}
          </div>
        </div>
        <div class="flex gap-2">
          <BaseButton
            @click="sendTestNotification"
            :disabled="isSendingTestNotification"
          >
            Send Test
          </BaseButton>
          <BaseButton @click="isTestNotificationPopupVisible = false">
            Close
          </BaseButton>
        </div>
      </div>
    </BasePopup>

    <!-- Generate Password Link Popup -->
    <BasePopup
      title="Generate Password Link"
      :is-visible="isPassLinkPopupVisible"
      :is-full-width="false"
      @close="isPassLinkPopupVisible = false"
      entity-name="Password Reset Link"
    >
      <div class="flex flex-col">
        <div class="mb-4">
          <p>
            Click the Generate Link button to create a password reset link for the user.
          </p>
          <Loading v-if="isGeneratingPasswordLink" />
        </div>
        <div class="my-4 mx-4" v-if="passLink">
          <a :href="passLink" class="font-bold font-mono text-xl text-blue-800 underline">
            Password Reset link for {{ person.firstName }} {{ person.lastName }}
          </a>
          <div class="text-sm ml-4 mt-4 text-gray-800 italic">
            (right-click on link and choose "Copy Link")
          </div>
        </div>
        <div class="flex">
          <BaseButton @click="generatePasswordLink" class="mr-2">
            Generate Link
          </BaseButton>
          <BaseButton @click="isPassLinkPopupVisible = false">Cancel</BaseButton>
        </div>
      </div>
    </BasePopup>

    <!-- New Password Popup -->
    <EditPopup
      entity-name="Password"
      :is-visible="isPasswordPopupVisible"
      :is-edit-mode="false"
      :save-error="passwordSaveError"
      @close="isPasswordPopupVisible = false"
      @save="saveNewPassword"
    >
      <BaseInput
        v-model="newPassword"
        :error="passwordErrors.newPassword"
        label="New Password"
        type="text"
      />
    </EditPopup>

    <!-- Edit Bulk Permissions Popup -->
    <EditPopup
      entity-name="Permissions in Bulk"
      :is-visible="isBulkAddPermissionsPopupOpen"
      :is-edit-mode="true"
      :save-error="saveError"
      :delete-enabled="false"
      @close="isBulkAddPermissionsPopupOpen = false"
      @save="saveBulkAddPermissions"
    >
      <BaseCheckboxGroup
        :model-value="bulkAddPermissionsSelectedPermissions"
        :options="portalPermissionOptions"
        label="Permissions"
        class="md:flex flex-wrap"
        checkbox-class="basis-1/2 xl:basis-1/3"
      />
      <div class="mt-4">
        <BaseSelect
          :options="client?.categoryValues"
          v-model="bulkAddPermissionsSelectedCategory"
          :label="`${client?.categoryAlias ?? 'Category'}`"
          :default-option="allCategoriesOption"
        />
      </div>
    </EditPopup>

    <!-- Edit Registrations Permissions Popup -->
    <EditPopup
      :entity-name="`Building Permissions for ${person?.firstName} ${person?.lastName}`"
      :is-visible="isRegistrationPermissionsPopupOpen"
      :is-edit-mode="true"
      :save-error="saveError"
      :delete-enabled="false"
      @close="closeRegistrationPermissionsPopup"
      @save="saveAllRegistrationPermissions"
    >
      <!-- Optional: Show save progress -->
      <div
        v-if="isSavingRegistrations"
        class="mb-4 p-3 bg-blue-100 border border-blue-300 rounded"
      >
        <p class="text-sm">
          Saving permissions for {{ portalRegistrationData.length }} portals...
        </p>
      </div>

      <!-- Loop through each portal -->
      <div
        v-for="portalData in portalRegistrationData"
        :key="portalData.portalId"
        class="mb-4"
      >
        <RegistrationPermissionsPanel
          v-if="person?.personId"
          :ref="(el) => setRegistrationPanelRef(portalData.portalId, el)"
          :portal-id="portalData.portalId"
          :portal-name="portalData.portalName"
          :person-id="person.personId"
          :initial-permissions="portalData.initialPermissions"
          :disabled="isSavingRegistrations"
          @save-success="handlePanelSaveSuccess(portalData.portalId)"
          @save-error="handlePanelSaveError(portalData.portalId, $event)"
        />
      </div>

      <!-- Optional: Show summary of dirty panels -->
      <div
        v-if="dirtyPanelCount > 0"
        class="mt-4 p-2 bg-yellow-50 border border-yellow-200 rounded"
      >
        <p class="text-sm text-gray-700">
          {{ dirtyPanelCount }} portal(s) have unsaved changes
        </p>
      </div>
    </EditPopup>

    <!-- Edit User Popup -->
    <EditPopup
      entity-name="User"
      :is-visible="isPopupVisible"
      :is-edit-mode="isEditing"
      :save-error="saveError"
      :delete-enabled="person.registrations?.length > 1"
      deleteButtonText="Delete Registration"
      @close="
        isPopupVisible = false;
        loadPerson();
      "
      @save="saveClick"
      @delete="deleteRegistrationClick"
    >
      <TabGroup>
        <TabList>
          <Tab v-slot="{ selected }">
            <button
              :class="[
                'w-full py-2 px-4 font-medium leading-5',
                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                selected
                  ? 'bg-blue-300 text-black shadow py-3'
                  : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
              ]"
            >
              General
            </button>
          </Tab>
          <Tab v-slot="{ selected }">
            <button
              :class="[
                'w-full py-2 px-4 font-medium leading-5',
                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                selected
                  ? 'bg-blue-300 text-black shadow py-3'
                  : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
              ]"
            >
              Alert
            </button>
          </Tab>
          <Tab v-slot="{ selected }">
            <button
              :class="[
                'w-full py-2 px-4 font-medium leading-5',
                'ring-white ring-opacity-60 ring-offset-2 ring-offset-blue-400 focus:outline-none focus:ring-2',
                selected
                  ? 'bg-blue-300 text-black shadow py-3'
                  : 'bg-gray-100 text-gray-800 hover:bg-blue-100  mb-1',
              ]"
            >
              Permissions
            </button>
          </Tab>
        </TabList>
        <TabPanels class="border border-blue-300 p-8">
          <TabPanel>
            <div class="sm:flex">
              <div class="basis-1/2 sm:pr-2">
                <BaseInput
                  v-model="personFirstName"
                  :error="errors.personFirstName"
                  label="First Name"
                  type="text"
                />
              </div>
              <div class="basis-1/2 sm:pl-2">
                <BaseInput
                  v-model="personLastName"
                  :error="errors.personLastName"
                  label="Last Name"
                  type="text"
                />
              </div>
            </div>
            <div class="sm:flex">
              <div class="basis-1/2 sm:pr-2">
                <BaseInput
                  v-model="personPrimaryPhone"
                  :error="errors.personPrimaryPhone"
                  label="Primary Phone"
                  type="text"
                />
              </div>
              <div class="basis-1/2 sm:pl-2">
                <BaseInput
                  v-model="personMobilePhone"
                  :error="errors.personMobilePhone"
                  label="Mobile Phone"
                  type="text"
                />
              </div>
            </div>
            <div class="mt-4" />

            <BaseCheckboxGroup
              :model-value="selectedTrainingProgramOptions"
              :options="trainingProgramOptions"
              label="Training Programs"
              class="md:flex flex-wrap"
              checkbox-class="basis-1/2 xl:basis-1/3"
            />

            <div class="mt-4" />
            <BaseSelect
              label="Tenant"
              v-model="personTenantId"
              :error="errors.personTenantId"
              :options="tenants"
              :default-option="({ name: '--Please Select--', value: 0 } as NameValue)"
            />
            <div class="xl:flex">
              <div class="basis-1/2 xl:pr-2">
                <BaseSelect
                  label="Primary Floor"
                  v-model="personPrimaryFloor"
                  :error="errors.personPrimaryFloor"
                  :options="property?.floorList"
                />
              </div>
              <div class="basis-1/2 xl:pl-2">
                <BaseSelect
                  label="Building Role"
                  v-model="personSiteRoleId"
                  :error="errors.personSiteRoleId"
                  :options="siteRoles"
                  :default-option="({ name: '--Please Select--', value: 0 } as NameValue)"
                />
                <div class="mt-2" />
              </div>
            </div>

            <div class="mt-4" />

            <div class="mt-2 md:flex flex-wrap">
              <div class="md:basis-1/2 xl:basis-1/3">
                <BaseCheckbox
                  v-model="personIsMobilityImpaired"
                  label="Has a Mobility Impairment"
                />
              </div>
              <div class="md:basis-1/2 xl:basis-1/3">
                <BaseCheckbox v-model="personIsTenantContact" label="Is Tenant Contact" />
              </div>
            </div>

            <div class="mt-4" />

            <div class="mt-2">
              <BaseCheckbox v-model="userIsDisabled" label="User Account is Inactive" />
            </div>
          </TabPanel>
          <TabPanel>
            <div class="lg:flex">
              <div class="basis-1/2 lg:pr-2">
                <BaseInput
                  v-model="personAlertSMS"
                  :error="errors.personAlertSMS"
                  label="Alert SMS"
                  type="text"
                />
              </div>
              <div class="basis-1/2 lg:pl-2">
                <BaseInput
                  v-model="personAlertEmail"
                  :error="errors.personAlertEmail"
                  label="Alert Email"
                  type="text"
                />
              </div>
            </div>
            <div>
              <p class="mt-6 mb-3 text-sm">
                Select floors that you want notifications for:
              </p>
              <div class="sm:flex mt-1">
                <div class="lg:grow-0 my-auto pr-4">
                  <BaseCheckbox v-model="selectAllAlertFloors" label="All Floors" />
                </div>

                <div class="sm:flex grow my-auto mt-2 sm:mt-0">
                  <Listbox
                    class="grow h-max"
                    v-model="selectedAlertFloors"
                    multiple
                    :disabled="selectAllAlertFloors"
                  >
                    <div class="relative">
                      <ListboxButton
                        class="relative w-full py-2 pl-3 pr-10 text-left bg-white border cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-opacity-75 focus-visible:ring-white focus-visible:ring-offset-orange-300 focus-visible:ring-offset-2 focus-visible:border-indigo-500 sm:text-sm"
                      >
                        <span
                          :class="[
                            selectAllAlertFloors ? 'text-gray-500' : 'text-gray-800',
                            'text-md',
                          ]"
                        >
                          {{
                            selectAllAlertFloors
                              ? "All floors selected"
                              : selectedAlertFloors.length > 0
                              ? selectedAlertFloors.join(", ")
                              : "-- select floors --"
                          }}
                        </span>
                        <span
                          class="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none"
                        >
                          <i-heroicons-solid-selector
                            :class="[
                              selectAllAlertFloors ? 'text-gray-300' : 'text-gray-600',
                              'w-5 h-5 text-2xl',
                            ]"
                            aria-hidden="true"
                          />
                        </span>
                      </ListboxButton>
                      <transition
                        leave-active-class="transition duration-100 ease-in"
                        leave-from-class="opacity-100"
                        leave-to-class="opacity-0"
                      >
                        <ListboxOptions
                          class="absolute w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm"
                        >
                          <ListboxOption
                            v-for="floor in property?.floorList"
                            :key="floor"
                            :value="floor"
                            as="template"
                          >
                            <li
                              :class="[
                                selectedAlertFloors.includes(floor)
                                  ? 'border-1 bg-sky-300 border-blue-500'
                                  : 'hover:bg-blue-200',
                                'text-gray-900 select-none relative py-1 pl-4 pr-4 cursor-pointer',
                              ]"
                            >
                              {{ floor }}
                            </li>
                          </ListboxOption>
                        </ListboxOptions>
                      </transition>
                    </div>
                  </Listbox>
                  <BaseButton
                    class="rounded-none text-sm"
                    @click="if (!selectAllAlertFloors) selectedAlertFloors = [];"
                  >
                    Clear Selection
                  </BaseButton>
                </div>
              </div>
            </div>

            <div class="mt-4" />
          </TabPanel>
          <TabPanel>
            <div class="md:basis-1/2 xl:basis-1/3">
              <BaseCheckbox
                v-model="personIsOldVrAdmin"
                label="Is an (legacy) Admin in VR"
              />
            </div>
            <div
              class="md:basis-1/2 xl:basis-1/3"
              v-if="
                property?.isStaffDirEnabled &&
                getSiteRoleById(personSiteRoleId)?.isStaffRole
              "
            >
              <BaseCheckbox
                v-model="personIsVisibleStaffDirectory"
                label="Is Visible in Staff Directory"
              />
            </div>

            <div class="md:basis-1/2 xl:basis-1/3">
              <BaseCheckbox v-model="personIsVrAdmin" label="Is an Admin in VR" />
            </div>

            <div class="mt-4" />

            <div class="md:basis-1/2 xl:basis-1/3">
              <BaseCheckbox
                v-model="isTenantAdmin"
                label="Is Tenant Admin instead of Portal Admin"
              />
            </div>

            <BaseCheckboxGroup
              :model-value="selectedPermissionOptions"
              :options="isTenantAdmin ? tenantPermissionOptions : portalPermissionOptions"
              label="Available Permissions"
              class="md:flex flex-wrap"
              checkbox-class="basis-1/2 xl:basis-1/3"
            />

            <div class="text-black mt-4">Multi-Portal Permissions</div>
            <div class="mt-1">
              <BaseCheckbox
                v-model="isHomePortal"
                :label="`Is Home Portal (${property.name})`"
              />
            </div>
            <div class="mt-1">
              <BaseCheckbox
                v-model="personIsGrantedAccessToClient"
                label="Has Access to All portals in Client"
              />
            </div>
            <div class="mt-4">
              <BaseSelect
                :options="client?.categoryValues"
                v-model="personIsGrantedAccessToCategory"
                :label="`Has Access to Portals in Client ${
                  client?.categoryAlias ?? 'Category'
                }`"
                :default-option="({ name: '----None----', value: '' } as NameValue)"
              />
            </div>
          </TabPanel>
        </TabPanels>
      </TabGroup>
    </EditPopup>
  </PortalLayout>
</template>

<script setup lang="ts">
import { TabGroup, TabList, Tab, TabPanels, TabPanel } from "@headlessui/vue";
import RegistrationPermissionsPanel from "../../components/RegistrationPermissionsPanel.vue";

import { useAdminStore } from "../../store/adminStore";
import { useAlertStore } from "../../store/alertStore";
import { useBldgStore } from "../../store/bldgStore";
import { usePersonStore } from "../../store/personStore";
import { useTrainingStore } from "../../store/trainingStore";
import { useUserStore } from "../../store/userStore";
import {
  BulkAddPermissionsRequest,
  PermissionType,
  SetNewPasswordDTO,
  StaffSavePersonCommand,
} from "../../store/generatedFetchClient";
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from "@headlessui/vue";
import {
  LoginHistoryDTO,
  PushSubscriptionDTO,
  RegistrationDTO,
  ResultsDTO,
} from "../../store/generatedFetchClient";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { NameValue } from "../../sharedTypes";
import { parseUserAgent } from "../../helpers";

let portalPermissionOptions = [
  { name: "Can View Training Report", value: PermissionType.ViewTrainingReport },
  { name: "Can View Portal Roster", value: PermissionType.ViewPortalRoster },
  { name: "Can View Mobility Report", value: PermissionType.ViewMobilityReport },
  { name: "Can View VR Report", value: PermissionType.ViewVRReport },
  { name: "Can View VR Filter Report", value: PermissionType.ViewVRFilterReport },
  {
    name: "Can View Tenant Contact Report",
    value: PermissionType.ViewTenantContactReport,
  },
  { name: "Can View Users", value: PermissionType.ViewUsers },
  { name: "Can Edit Users", value: PermissionType.EditUsers },
  { name: "Can Edit Documents", value: PermissionType.EditDocuments },
  { name: "Can Edit Contacts", value: PermissionType.EditContacts },
  { name: "Can View Alert Subscriptions", value: PermissionType.ViewAlertSubscriptions },
  { name: "Can Send Alerts", value: PermissionType.SendAlerts },
  { name: "Can Edit Respond Content", value: PermissionType.EditRespond },
  { name: "Can Edit Tenants", value: PermissionType.EditTenant },
];

let tenantPermissionOptions = [
  { name: "Can View Training Report", value: PermissionType.ViewTrainingReport },
  { name: "Can View Tenant Roster", value: PermissionType.ViewPortalRoster },
  { name: "Can View Mobility Report", value: PermissionType.ViewMobilityReport },
  { name: "Can View VR Report", value: PermissionType.ViewVRReport },
  { name: "Can View VR Filter Report", value: PermissionType.ViewVRFilterReport },
  { name: "Can View Users", value: PermissionType.ViewUsers },
  { name: "Can Edit Users", value: PermissionType.EditUsers },
];

const alertStore = useAlertStore();
const bldgStore = useBldgStore();
const personStore = usePersonStore();
const trainingStore = useTrainingStore();
const adminStore = useAdminStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

var isPersonLoading = ref(true);
var pushSubscriptions = ref<PushSubscriptionDTO[]>([]);
var isDeletingSubscription = ref(false);
var client = computed(() =>
  adminStore.clients?.find((c) => c.clientId === property.value?.clientId)
);
var property = computed(() => bldgStore.property);
var trainingPrograms = computed(() => trainingStore.portalTrainingPrograms);

let person = computed(() => (isPersonLoading.value ? {} : personStore.person));

onMounted(loadPerson);
watch(property, loadPerson);

async function loadPerson() {
  isPersonLoading.value = true;
  await adminStore.getClientList();
  if (property?.value?.propertyId) {
    if (route.params.personId && typeof route.params.personId === "string") {
      let personId = Number.parseInt(route.params.personId);
      console.log(`loading data for user ${personId}`);
      let loadedPerson = await personStore.getPerson(personId);

      await trainingStore.getTrainingProgramsByPortal(property.value?.portalId);
      if (loadedPerson == null) {
        isPersonLoading.value = false;
        return;
      }

      let permissions = loadedPerson.registrations?.find(
        (r) => r.portalId === property.value?.portalId
      ).permissions;
      selectedTrainingProgramOptions.value = permissions
        .filter((p) => p.trainingProgramId != null)
        .map((p) => p.trainingProgramId);
      currentPermissions.value = permissions
        .filter((p) => p.permissionType != null)
        .map((p) => p.permissionType);
      selectedPermissionOptions.value = currentPermissions.value;

      personIsGrantedAccessToCategory.value = loadedPerson.grantedAccessToRegion;
      personIsGrantedAccessToClient.value = loadedPerson.grantedAccessToClient;

      userIsDisabled.value =
        (loadedPerson.dateDisabled != null && loadedPerson.dateDisabled != undefined) ??
        false;
      isHomePortal.value = portalRegistration.value?.isHomePortal;

      isPersonLoading.value = false;
      console.log(`Loaded person`, loadedPerson);

      // Load push notification device registrations
      pushSubscriptions.value = await alertStore.getPushSubscriptions(personId);
    }
  }
}

// #region Push Notification Devices
async function deletePushSubscription(pushSubscriptionId: number) {
  isDeletingSubscription.value = true;
  let error = await alertStore.deletePushSubscription(pushSubscriptionId);
  if (!error) {
    if (person.value?.personId) {
      pushSubscriptions.value = await alertStore.getPushSubscriptions(person.value.personId);
    }
  } else {
    saveError.value = error;
  }
  isDeletingSubscription.value = false;
}

var isTestNotificationPopupVisible = ref(false);
var isSendingTestNotification = ref(false);
var testNotificationResult = ref("");
var testNotificationSubscriptionId = ref<number | null>(null);
var testNotificationDevice = ref("");
var testNotificationBody = ref("");

function openTestNotificationPopup(sub: PushSubscriptionDTO) {
  testNotificationSubscriptionId.value = sub.pushSubscriptionId ?? null;
  testNotificationDevice.value = parseUserAgent(sub.userAgent);
  testNotificationResult.value = "";
  testNotificationBody.value = "This is a test notification sent on " + new Date().toLocaleString();
  isTestNotificationPopupVisible.value = true;
}

async function sendTestNotification() {
  if (!testNotificationSubscriptionId.value) return;

  isSendingTestNotification.value = true;
  testNotificationResult.value = "";

  let error = await alertStore.sendTestPushNotification({
    pushSubscriptionId: testNotificationSubscriptionId.value,
    title: `SafetyNet Test - ${property.value?.name ?? ""}`,
    body: testNotificationBody.value,
    linkUrl: `${location.origin}/${property.value?.siteCode ?? ""}`,
  });

  if (!error) {
    testNotificationResult.value = "success";
    // if (person.value?.personId) {
    //   pushSubscriptions.value = await alertStore.getPushSubscriptions(
    //     person.value.personId
    //   );
    // }
  } else {
    testNotificationResult.value = error;
  }

  isSendingTestNotification.value = false;
}
// #endregion Push Notification Devices

// #region Edit user
let isPopupVisible = ref(false);
let isEditing = ref(false);
let saveError = ref("");

const { handleSubmit, errors } = useForm({
  validationSchema: yup.object({
    personFirstName: yup.string().required().max(50).label("First Name"),
    personLastName: yup.string().notRequired().max(50).label("Last Name"),
    personLoginEmail: yup.string().required().email().max(255).label("Login Email"),
    personPrimaryFloor: yup
      .string()
      .nullable()
      .notRequired()
      .max(50)
      .label("Primary Floor"),
    personSiteRoleId: yup
      .number()
      .min(1, "Building Role is a required field")
      .required()
      .label("Building Role"),
    personTenantId: yup
      .number()
      .required()
      .test(
        "valid-tenant-vr",
        (d) => `Tenant is required for VR-enabled portals`,
        (value) => (property.value?.isVrtourEnabled ? value > 0 : true)
      )
      .label("Tenant"),
    personPrimaryPhone: yup
      .string()
      .nullable()
      .notRequired()
      .max(50)
      .label("Primary Phone"),
    personMobilePhone: yup
      .string()
      .nullable()
      .notRequired()
      .max(50)
      .label("Mobile Phone"),
    personAlertSMS: yup.string().nullable().notRequired(),
    personAlertEmail: yup.string().nullable().notRequired(),
  }),
});

const { value: personFirstName } = useField<string>("personFirstName");
const { value: personLastName } = useField<string>("personLastName");
const { value: personLoginEmail } = useField<string>("personLoginEmail");
const { value: personPrimaryFloor } = useField<string | null>("personPrimaryFloor");
const { value: personTenantId } = useField<number>("personTenantId");
const { value: personSiteRoleId } = useField<number>("personSiteRoleId");
const { value: personPrimaryPhone } = useField<string | null>("personPrimaryPhone");
const { value: personMobilePhone } = useField<string | null>("personMobilePhone");
const personIsMobilityImpaired = ref(false);
const personIsGrantedAccessToClient = ref(false);
const personIsGrantedAccessToCategory = ref<string | null>(null);
const personIsVrAdmin = ref(false);
const personIsOldVrAdmin = ref(false);

const personIsTenantContact = ref(false);
const personIsVisibleStaffDirectory = ref(false);
const { value: personAlertSMS } = useField<string | null>("personAlertSMS");
const { value: personAlertEmail } = useField<string | null>("personAlertEmail");
const selectAllAlertFloors = ref(false);
const selectedAlertFloors = ref([] as string[]);
const userIsDisabled = ref(false);
const isHomePortal = ref(false);

let portalRegistration = computed(() =>
  isPersonLoading.value
    ? ({} as RegistrationDTO)
    : person.value?.registrations?.find((r) => r.portalId === property.value?.portalId)
);

let portalSiteRole = computed(() =>
  property.value?.siteRoles?.find(
    (r) => r.siteRoleId === portalRegistration.value?.siteRoleId
  )
);

let siteRoles = computed(() =>
  isPersonLoading.value
    ? ([] as NameValue[])
    : property.value?.siteRoles?.map(
        (r) =>
          ({
            name: r.roleAlias + (r.isStaffRole ? " (Staff)" : ""),
            value: r.siteRoleId,
          } as NameValue)
      )
);

let tenants = computed(() =>
  isPersonLoading.value
    ? ([] as NameValue[])
    : property.value?.tenants?.map(
        (t) => ({ name: t.name, value: t.tenantId } as NameValue)
      )
);

let trainingProgramOptions = computed(() =>
  isPersonLoading.value
    ? ([] as NameValue[])
    : trainingPrograms.value?.map(
        (p) =>
          ({
            name: p?.name,
            value: p?.trainingProgramId,
          } as NameValue)
      )
);
let selectedTrainingProgramOptions = ref<number[]>();
const isTenantAdmin = ref(false);
let currentPermissions = ref<number[]>();
let selectedPermissionOptions = ref<number[]>();

let lastFiveResults = computed(() =>
  isPersonLoading.value ? ([] as ResultsDTO[]) : person.value?.results?.slice(0, 5)
);
let lastFiveLogins = computed(() =>
  isPersonLoading.value
    ? ([] as LoginHistoryDTO[])
    : person.value?.loginHistories?.slice(0, 5)
);

let allRegistrations = computed(() =>
  person.value?.registrations.map((r) => ({
    ...r,
    ...getPortalById(r.portalId),
  }))
);

let saveClick = handleSubmit(onValidSubmit, onInvalidSubmit);

async function onValidSubmit() {
  let savePerson = {
    personId: person.value?.personId,
    portalId: property.value?.portalId,
    isHomePortal: isHomePortal.value,
    loginEmail: personLoginEmail.value,
    firstName: personFirstName.value,
    lastName: personLastName.value,
    primaryFloor: personPrimaryFloor.value,
    siteRoleId: personSiteRoleId.value,
    mobilePhone: personMobilePhone.value,
    tenantId: personTenantId.value > 0 ? personTenantId.value : null,
    primaryPhone: personPrimaryPhone.value,
    contactEmail: personAlertEmail.value,
    contactSms: personAlertSMS.value,
    alertSubscribedFloors: selectAllAlertFloors.value
      ? null
      : selectedAlertFloors.value?.toString(),
    isTenantContact: personIsTenantContact.value,
    isTenantAdmin: isTenantAdmin.value,
    isVrAdmin: personIsVrAdmin.value,
    isOldVrAdmin: personIsOldVrAdmin.value,

    isVisibleStaffDirectory: personIsVisibleStaffDirectory.value,
    trainingProgramIds: selectedTrainingProgramOptions.value,
    permissionTypeIds: selectedPermissionOptions.value,
    isMobilityImpaired: personIsMobilityImpaired.value,
    grantedAccessToRegion: personIsGrantedAccessToCategory.value,
    grantedAccessToClient: personIsGrantedAccessToClient.value,
    dateDisabled: userIsDisabled.value ? new Date() : undefined,
  } as StaffSavePersonCommand;

  console.log("Saving person", savePerson);
  saveError.value = "";

  if (property.value && property.value?.portalId) {
    let possibleError = await personStore.staffSavePerson(savePerson);
    if (!possibleError) {
      loadPerson();
      isPopupVisible.value = false;
    } else {
      //handle error
      saveError.value = possibleError;
    }
  }
}

function onInvalidSubmit(ctx: any) {
  saveError.value = "One or more invalid form inputs.";
  console.log("error trying to submit form", ctx);
}

function editUser() {
  console.log("Editing user", person.value);
  if (person.value?.personId > 0) {
    personFirstName.value = person.value?.firstName;
    personLastName.value = person.value?.lastName;
    personLoginEmail.value = person.value?.loginEmail;
    personPrimaryFloor.value = person.value?.primaryFloor;
    personPrimaryPhone.value = person.value?.primaryPhone;
    personMobilePhone.value = person.value?.mobilePhone;
    personIsMobilityImpaired.value = person.value?.isMobilityImpaired;
    personIsGrantedAccessToCategory.value = person.value?.grantedAccessToRegion;
    personIsGrantedAccessToClient.value = person.value?.grantedAccessToClient;
    personIsVrAdmin.value = person.value?.isVrAdmin ?? false;
    personIsOldVrAdmin.value = person.value?.isOldVrAdmin ?? false;

    personIsTenantContact.value = person.value?.isTenantContact ?? false;
    personAlertEmail.value = person.value?.contactEmail;
    personAlertSMS.value = person.value?.contactSms;
    selectAllAlertFloors.value =
      person.value?.alertSubscribedFloors == property?.value?.floorList.toString() ||
      person.value?.alertSubscribedFloors == null;
    selectedAlertFloors.value =
      person.value?.alertSubscribedFloors != null &&
      person.value?.alertSubscribedFloors.length > 0
        ? person.value?.alertSubscribedFloors.split(",")
        : [];

    selectedPermissionOptions.value = currentPermissions.value;
    isTenantAdmin.value = portalRegistration.value.isTenantAdmin;
    personSiteRoleId.value = portalRegistration.value?.siteRoleId;
    personTenantId.value = portalRegistration.value?.tenantId;
    personIsVisibleStaffDirectory.value =
      portalRegistration.value?.isVisibleStaffDirectory;
    selectedTrainingProgramOptions.value = trainingPrograms.value
      .filter((t) =>
        portalRegistration.value?.permissions.find(
          (p) => p.trainingProgramId === t.trainingProgramId
        )
      )
      .map((p) => p.trainingProgramId);

    isHomePortal.value = portalRegistration.value?.isHomePortal;
    userIsDisabled.value =
      (person.value?.dateDisabled !== null && person.value?.dateDisabled !== undefined) ??
      false;
    saveError.value = "";
    isEditing.value = true;
    isPopupVisible.value = true;
  }
}

function formatDateTime(date: any) {
  let d = new Date(date);
  let formatedDate =
    d.toDateString() +
    " " +
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return formatedDate === "Invalid Date" ? "" : formatedDate;
}

function formatDateTimeLocal(date: any) {
  if (!date) return "";
  let dateStr = typeof date === "string" && !date.endsWith("Z") ? date + "Z" : date;
  let d = new Date(dateStr);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString([], {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

// #endregion Edit user

// #region Password Link Popup
let isPassLinkPopupVisible = ref(false);
let isGeneratingPasswordLink = ref(false);
let passLink = ref("");

function openPassLinkPopup() {
  passLink.value = "";
  passwordSaveError.value = "";

  isPassLinkPopupVisible.value = true;
}

async function generatePasswordLink() {
  passwordSaveError.value = "";
  passLink.value = "";
  if (person.value?.loginEmail && property.value?.siteCode) {
    isGeneratingPasswordLink.value = true;
    passLink.value = await adminStore.generatePasswordResetLink(
      person.value?.loginEmail,
      property.value?.siteCode
    );
    isGeneratingPasswordLink.value = false;
  }
}
// #endregion Password Link Popup

// #region New Password Popup

let passwordSaveError = ref("");
let isPasswordPopupVisible = ref(false);

const { handleSubmit: handlePasswordSubmit, errors: passwordErrors } = useForm({
  validationSchema: yup.object({
    newPassword: yup.string().required().min(2).max(50).label("Password"),
  }),
});

const { value: newPassword } = useField<string>("newPassword");

function openPasswordPopup() {
  newPassword.value = "";
  passwordSaveError.value = "";

  isPasswordPopupVisible.value = true;
}

let saveNewPassword = handlePasswordSubmit(
  onValidPasswordSubmit,
  onInvalidPasswordSubmit
);

async function onValidPasswordSubmit() {
  let savePassword = {
    newPassword: newPassword.value,
    email: person.value?.loginEmail,
  } as SetNewPasswordDTO;

  console.log("Setting new password ", savePassword);

  passwordSaveError.value = await adminStore.setNewPassword(savePassword);
  if (!passwordSaveError.value) {
    isPasswordPopupVisible.value = false;
  }
}

function onInvalidPasswordSubmit(ctx: any) {
  passwordSaveError.value = "Invalid form input.";
  console.log("error trying to submit form", ctx);
}

// #endregion New Password Popup

//#region Registration Permissions Popup

const isRegistrationPermissionsPopupOpen = ref(false);
const isSavingRegistrations = ref(false);
const registrationPanelRefs = ref<Map<number, any>>(new Map());

// Track which panels have been saved
const savedPanels = ref<Set<number>>(new Set());
const errorPanels = ref<Map<number, string>>(new Map());

// Computed: Get portals in client with their current permissions
const portalRegistrationData = computed(() => {
  if (!client.value) return [];

  const buildingsInClient = bldgStore.properties?.filter(
    (p) => p.clientId === client.value?.clientId
  );

  return buildingsInClient?.map((portal) => {
    // Find existing registration for this portal
    const registration = person.value?.registrations?.find(
      (r) => r.portalId === portal.portalId
    );

    // Extract current permissions
    const currentPermissions =
      registration?.permissions
        ?.filter((p) => p.permissionType != null)
        .map((p) => p.permissionType) ?? [];

    return {
      portalId: portal.portalId,
      portalName: portal.name,
      siteCode: portal.siteCode,
      category: portal.category,
      initialPermissions: currentPermissions,
      hasRegistration: !!registration,
    };
  }) ?? [];
});

// Computed: Count dirty panels
const dirtyPanelCount = computed(() => {
  let count = 0;
  registrationPanelRefs.value.forEach((panel) => {
    if (panel?.isDirty?.()) count++;
  });
  return count;
});

// Helper: Store panel refs in a map
function setRegistrationPanelRef(portalId: number, el: any) {
  if (el) {
    registrationPanelRefs.value.set(portalId, el);
  } else {
    registrationPanelRefs.value.delete(portalId);
  }
}

function openRegistrationPermissions() {
  saveError.value = "";
  savedPanels.value.clear();
  errorPanels.value.clear();
  isRegistrationPermissionsPopupOpen.value = true;
}

function closeRegistrationPermissionsPopup() {
  isRegistrationPermissionsPopupOpen.value = false;
  registrationPanelRefs.value.clear();
}

// Called when popup Save button clicked
async function saveAllRegistrationPermissions() {
  saveError.value = "";
  isSavingRegistrations.value = true;
  savedPanels.value.clear();
  errorPanels.value.clear();

  // Get all dirty panels
  const dirtyPanels: Array<{ portalId: number; panel: any }> = [];
  registrationPanelRefs.value.forEach((panel, portalId) => {
    if (panel?.isDirty?.()) {
      dirtyPanels.push({ portalId, panel });
    }
  });

  if (dirtyPanels.length === 0) {
    // Nothing to save
    isSavingRegistrations.value = false;
    closeRegistrationPermissionsPopup();
    return;
  }

  // Save all dirty panels in parallel
  const savePromises = dirtyPanels.map(({ panel }) => panel.save());
  const results = await Promise.allSettled(savePromises);

  // Check results
  let hasErrors = false;
  results.forEach((result, index) => {
    const portalId = dirtyPanels[index].portalId;
    if (result.status === "rejected" || result.value === false) {
      hasErrors = true;
      errorPanels.value.set(portalId, "Save failed");
    } else {
      savedPanels.value.add(portalId);
    }
  });

  isSavingRegistrations.value = false;

  if (hasErrors) {
    const errorCount = errorPanels.value.size;
    saveError.value = `Failed to save ${errorCount} portal(s). Please check individual errors.`;
  } else {
    // Success! Reload person data and close
    await loadPerson();
    closeRegistrationPermissionsPopup();
  }
}

// Individual panel save handlers (optional - for real-time feedback)
function handlePanelSaveSuccess(portalId: number) {
  savedPanels.value.add(portalId);
  errorPanels.value.delete(portalId);
}

function handlePanelSaveError(portalId: number, error: string) {
  errorPanels.value.set(portalId, error);
}

//#endregion Registration Permissions Popup

// #region Bulk add permissions

const allCategoriesOption = computed(
  () =>
    `----Add User Permissions for Portals in Every ${
      client?.value?.categoryAlias ?? "Category"
    }----`
);
let isBulkAddPermissionsPopupOpen = ref(false);
let bulkAddPermissionsSelectedPermissions = ref([] as PermissionType[]);
let bulkAddPermissionsSelectedCategory = ref(allCategoriesOption.value);

function openBulkAddPermissions() {
  bulkAddPermissionsSelectedPermissions.value = [];
  bulkAddPermissionsSelectedCategory.value = allCategoriesOption.value;

  saveError.value = "";
  isBulkAddPermissionsPopupOpen.value = true;
}

async function saveBulkAddPermissions() {
  saveError.value = "";

  if (bulkAddPermissionsSelectedCategory.value == allCategoriesOption.value)
    bulkAddPermissionsSelectedCategory.value = null;

  let request = {
    personId: person.value?.personId,
    clientId: client.value?.clientId,
    clientCategory: bulkAddPermissionsSelectedCategory.value,
    permissions: bulkAddPermissionsSelectedPermissions.value,
  } as BulkAddPermissionsRequest;

  saveError.value = await personStore.bulkAddPermissions(request);
  if (!saveError.value) {
    loadPerson();
    isBulkAddPermissionsPopupOpen.value = false;
  }
}

watch(isTenantAdmin, tenantAdminSelectionChange);
function tenantAdminSelectionChange() {
  if (isTenantAdmin.value) {
    selectedPermissionOptions.value = currentPermissions.value.filter((p) =>
      tenantPermissionOptions.map((tpo) => tpo.value).includes(p)
    );
  } else {
    selectedPermissionOptions.value = currentPermissions.value;
  }
}
// #endregion Bulk add permissions

// #region Delete Registration
async function deleteRegistrationClick() {
  let result = await personStore.deleteRegistration(
    person.value.personId,
    property.value.propertyId
  );

  if (result == null) {
    await personStore.getForPortal(property?.value?.portalId, true);
    router.push(`../users`);
  } else {
    saveError.value = result;
  }
}
// #endregion Delete Registration

// #region Login as user
async function loginAsUser() {
  var loginVM = await userStore.impersonateUser(
    person.value.loginEmail,
    property.value.portalId,
    property.value.clientSystemName,
    null,
    property.value.siteCode
  );

  if (!loginVM.error) router.push("/" + property.value.siteCode);
}
// #endregion Login as user

// #region force verify user
async function forceVerifyEmail() {
  saveError.value = "";
  saveError.value = await userStore.staffForceVerifyEmail(person.value.loginEmail);

  if (!saveError.value) {
    loadPerson();
  }
}
// #endregion force verify user

// #region Helpers

var getSiteRoleById = (siteRoleId: number) =>
  property.value?.siteRoles?.find((r) => r.siteRoleId == siteRoleId);

var getPortalById = (portalId: number) =>
  bldgStore.properties?.find((p) => p.portalId == portalId);

let isCopyClicked = ref(false);
var copyEmailToClipboard = () => {
  navigator.clipboard.writeText(person.value.loginEmail);
  isCopyClicked.value = true;
};

// #endregion Helpers
</script>
